import { createContext, useContext, useEffect, useState } from "react";

const LanguageContext = createContext(null);

export const LANGS = [
  { code: "en", label: "EN", full: "English" },
  { code: "gu", label: "ગુ", full: "ગુજરાતી" },
  { code: "hi", label: "हि", full: "हिन्दी" },
];

export const TR = {
  en: {
    nav: { home: "Home", about: "About", expertise: "Expertise", research: "Research", media: "Media", contact: "Contact", consult: "Consult" },
    hero: {
      kicker: "Interventional Cardiologist",
      l1: "Advanced", l2: "Cardiac Care", l3: "with Precision.",
      desc: "Dr. Devratsinh Parmar combines the latest medical research with advanced interventional techniques to treat complex heart and vascular conditions — with empathy at every step.",
      cta1: "Explore Expertise", cta2: "Connect", scroll: "Scroll",
    },
    stats: [
      { value: "15+", label: "Peer-Reviewed Publications" },
      { value: "24/7", label: "Cardiac Availability" },
      { value: "DM", label: "Doctorate in Cardiology" },
      { value: "CSI", label: "National Conference Presenter" },
    ],
    about: {
      kicker: "About Dr. Parmar",
      title: "A clinician devoted to the complex heart",
      role: "Assistant Professor in Cardiology",
      city: "Ahmedabad, Gujarat, India",
      body: "Dr. Devratsinh Parmar is a dedicated cardiologist with a passion for managing complex and rare cardiovascular diseases. Currently serving as an Assistant Professor in Cardiology at the prestigious U.N. Mehta Institute of Cardiology and Research Centre in Ahmedabad, he is deeply involved in both advanced patient care and the education of future cardiologists.",
      readMore: "Read Full Story",
    },
    aboutPage: {
      kicker: "About Dr. Parmar",
      t1: "Meet Dr.", t2: "Devratsinh Parmar",
      subtitle: "MBBS · MD (General Medicine) · DM (Cardiology) — a dedicated cardiologist with a passion for managing complex and rare cardiovascular diseases.",
      lead: "Currently serving as an Assistant Professor in Cardiology at the prestigious U.N. Mehta Institute of Cardiology and Research Centre (UNMICRC), Dr. Parmar is deeply involved in both advanced patient care and the education of future cardiologists.",
      lead2: "His clinical approach is rooted in evidence-based medicine, ensuring that every patient receives a tailored, highly effective treatment plan — from routine cardiology to advanced endovascular procedures and the management of rare cardiomyopathies and arrhythmias.",
      videoKicker: "From the Cath Lab",
      videoTitle: "Tips & Tricks in Difficult BMV",
      mediaLink: "Media Library",
    },
    chapters: [
      { num: "1", title: "The Clinician", body: "Dr. Devratsinh Parmar is a dedicated cardiologist with a passion for managing complex and rare cardiovascular diseases. Currently serving as an Assistant Professor in Cardiology at the prestigious U.N. Mehta Institute of Cardiology and Research Centre in Ahmedabad, he is deeply involved in both advanced patient care and the education of future cardiologists." },
      { num: "2", title: "The Foundation", body: "With comprehensive medical training — an MBBS, an MD in General Medicine, and a DM in Cardiology — Dr. Parmar has built a strong foundation in diagnosing and treating high-risk cardiac conditions. His clinical approach is rooted in evidence-based medicine, ensuring every patient receives a tailored, highly effective treatment plan." },
      { num: "3", title: "The Specialist", body: "Beyond routine cardiology, Dr. Parmar holds special expertise in advanced endovascular procedures such as Endovascular Aneurysm Repair (EVAR) and Carotid Artery Stenting, as well as the management of rare cardiomyopathies and complex arrhythmias." },
    ],
    award: {
      kicker: "Recognition",
      badge: "Awards & Recognition",
      heading: "Awards & Recognition",
      sub: "Honoured for excellence in interventional cardiology, academic leadership and compassionate patient care.",
    },
    services: {
      kicker: "Clinical Expertise",
      title1: "Specialized Cardiac", title2: "Treatments",
      viewAll: "View All",
      pageTitle1: "Specialized", pageTitle2: "Cardiac Care",
      pageSub: "From complex catheter-based intervention to preventive risk assessment — evidence-based treatment across the full cardiovascular spectrum.",
      ctaTitle: "Discuss your cardiac concern today.",
      viewResearch: "View Research",
      items: [
        { title: "Interventional Cardiology & Vascular Procedures", body: "Expertise in complex catheter-based treatments, including Carotid Artery Stenting and Endovascular Aneurysm Repair (EVAR) for difficult vascular access cases." },
        { title: "Heart Failure & Cardiomyopathy Management", body: "Advanced diagnostic and therapeutic management of restrictive cardiomyopathies, severe left ventricular (LV) dysfunction, and conditions related to systemic diseases such as Monoclonal Gammopathy." },
        { title: "Acute Coronary Syndromes & Ischemic Heart Disease", body: "Comprehensive care for heart attacks, angina, and unique cardiac events like Kounis Syndrome — allergic acute coronary syndrome." },
        { title: "Arrhythmia & Device Implantation", body: "Management of life-threatening arrhythmias such as recurrent ventricular tachycardia, and implantation of life-saving devices including Automatic Implantable Cardioverter-Defibrillators (AICD)." },
        { title: "Preventive Cardiology & Risk Assessment", body: "Utilising the latest clinical scoring systems to evaluate traditional risk factors and prevent coronary artery disease before it becomes critical." },
      ],
    },
    research: {
      kicker: "Academic Contributions",
      title: "Advancing Cardiovascular Science",
      viewAll: "All Publications & Case Reports",
      pageTitle1: "Research &", pageTitle2: "Publications",
      pageSub: "At the forefront of continuous cardiac education — presenting at the Cardiological Society of India (CSI) and contributing to international peer-reviewed journals.",
      pubHeading: "Selected High-Impact Publications",
      caseHeading: "Notable Case Reports & Presentations",
      videoKicker: "Educational Session",
      videoTitle: "Tips & Tricks in Difficult Balloon Mitral Valvuloplasty",
      videoSub: "A practical procedural walkthrough sharing techniques for navigating challenging BMV cases in the cath lab.",
      caseLabel: "Case",
    },
    media: {
      kicker: "Video & Media",
      title: "Tips & Tricks in Difficult BMV",
      link: "Media Library",
      pageTitle1: "The Media", pageTitle2: "Library",
      pageSub: "Educational cardiology content and procedural insights, shared for the next generation of cardiologists.",
      featured: "Featured",
      h: [
        { k: "Topic", v: "Balloon Mitral Valvuloplasty" },
        { k: "Format", v: "Procedural Teaching" },
        { k: "Audience", v: "Cardiology Fellows & Peers" },
      ],
    },
    testimonials: {
      kicker: "Patient Trust",
      title: "Words from those he has cared for",
      items: [
        { name: "Ramesh Patel", place: "Ahmedabad", text: "Dr. Parmar explained my angioplasty calmly and clearly. Two years on, my heart feels stronger than ever. Forever grateful." },
        { name: "Sunita Shah", place: "Vadodara", text: "He detected a rare cardiac condition others had missed. His precision and warmth truly saved my life." },
        { name: "Imran Sheikh", place: "Rajkot", text: "After my father's AICD implant, Dr. Parmar was available at every hour. Rare to find a doctor this dedicated." },
        { name: "Kiran Desai", place: "Surat", text: "From diagnosis to recovery, I always felt in safe, expert hands. He treats patients like family." },
        { name: "Meena Joshi", place: "Gandhinagar", text: "Compassionate, thorough and honest. He never rushed a single consultation. Highly recommended." },
      ],
    },
    contact: {
      kicker: "Connect With Me",
      title: "Reach Out",
      pageSub: "Available around the clock for cardiac consultation and referrals.",
      call: "Call Directly", email: "Send an Email",
      hoursLabel: "Working Hours", hoursNote: "Always available for cardiac emergencies.",
      locLabel: "Primary Location",
      locName: "U. N. Mehta Institute of Cardiology & Research Centre",
      locAddr: "Civil Hospital Campus, Asarwa, Ahmedabad, Gujarat",
      dirLabel: "Directions", maps: "Open in Google Maps",
      emergency: "Emergency Contact:",
      emergencyBody: "In case of a cardiac emergency, please proceed immediately to the nearest hospital casualty department.",
    },
    footer: { care: "Cardiovascular Care", navigate: "Navigate", reach: "Reach Out", tagline: "Precision · Research · Empathy",
      bio: "Assistant Professor at U.N. Mehta Institute of Cardiology & Research Centre. Advanced Cardiovascular Care with Precision and Empathy." },
    whatsapp: "Chat on WhatsApp",
    topbarRole: "Interventional Cardiologist",
    hospital: {
      kicker: "Leadership",
      role: "Director",
      title: "Godhra Heart & Super Speciality Hospital",
      tagline: "Where Every Heartbeat Matters.",
      lead: "Dr. Devratsinh Parmar is the Director of Godhra Heart & Super Speciality Hospital LLP — the only corporate multi-superspeciality 100-bedded hospital in Godhra city, delivering world-class medical care under one roof.",
      body: "Built on a vision of accessible excellence, the hospital brings advanced cardiac care, surgical expertise and critical-care infrastructure to the people of Godhra and the wider Panchmahal region — with compassion at the heart of every treatment.",
      beds: "Bedded Hospital",
      corporate: "Corporate Multi-Superspeciality",
      roof: "World-Class Facilities Under One Roof",
      scopeTitle: "Our Scope of Services",
      services: ["Interventional Cardiology", "Non-Interventional Cardiology", "Cardiothoracic & Vascular Surgery", "ENT", "Head & Neck Oncology", "Oncology Surgery", "General Medicine", "General & Laparoscopic Surgery", "Maxillofacial Surgery", "Critical Care"],
      addressLabel: "Address",
      callLabel: "Hospital Enquiry",
    },
  },

  gu: {
    nav: { home: "હોમ", about: "પરિચય", expertise: "નિપુણતા", research: "સંશોધન", media: "મીડિયા", contact: "સંપર્ક", consult: "સલાહ" },
    hero: {
      kicker: "ઇન્ટરવેન્શનલ કાર્ડિયોલોજિસ્ટ",
      l1: "એડવાન્સ્ડ", l2: "કાર્ડિયાક કેર", l3: "ચોકસાઈ સાથે.",
      desc: "ડૉ. દેવરતસિંહ પરમાર જટિલ હૃદય અને વાહિની રોગોની સારવાર માટે નવીનતમ તબીબી સંશોધન અને અદ્યતન ઇન્ટરવેન્શનલ ટેકનિકનો ઉપયોગ કરે છે — દરેક પગલે સહાનુભૂતિ સાથે.",
      cta1: "નિપુણતા જુઓ", cta2: "સંપર્ક કરો", scroll: "સ્ક્રોલ",
    },
    stats: [
      { value: "15+", label: "પીઅર-રિવ્યૂડ પ્રકાશનો" },
      { value: "24/7", label: "કાર્ડિયાક ઉપલબ્ધતા" },
      { value: "DM", label: "કાર્ડિયોલોજીમાં ડોક્ટરેટ" },
      { value: "CSI", label: "રાષ્ટ્રીય કોન્ફરન્સ પ્રસ્તુતકર્તા" },
    ],
    about: {
      kicker: "ડૉ. પરમાર વિશે",
      title: "જટિલ હૃદય માટે સમર્પિત ચિકિત્સક",
      role: "કાર્ડિયોલોજીમાં સહાયક પ્રાધ્યાપક",
      city: "અમદાવાદ, ગુજરાત, ભારત",
      body: "ડૉ. દેવરતસિંહ પરમાર જટિલ અને દુર્લભ હૃદયરોગોના સંચાલનમાં ઊંડો રસ ધરાવતા સમર્પિત કાર્ડિયોલોજિસ્ટ છે. હાલમાં અમદાવાદની પ્રતિષ્ઠિત યુ.એન. મહેતા ઇન્સ્ટિટ્યૂટ ઑફ કાર્ડિયોલોજીમાં સહાયક પ્રાધ્યાપક તરીકે સેવા આપતા, તેઓ અદ્યતન દર્દી સંભાળ અને ભાવિ કાર્ડિયોલોજિસ્ટના શિક્ષણ બંનેમાં ઊંડાણથી સંકળાયેલા છે.",
      readMore: "સંપૂર્ણ વાર્તા વાંચો",
    },
    aboutPage: {
      kicker: "ડૉ. પરમાર વિશે",
      t1: "મળો ડૉ.", t2: "દેવરતસિંહ પરમાર",
      subtitle: "MBBS · MD · DM (કાર્ડિયોલોજી) — જટિલ અને દુર્લભ હૃદયરોગોના સંચાલનમાં ઊંડો રસ ધરાવતા સમર્પિત કાર્ડિયોલોજિસ્ટ.",
      lead: "હાલમાં પ્રતિષ્ઠિત યુ.એન. મહેતા ઇન્સ્ટિટ્યૂટ ઑફ કાર્ડિયોલોજી (UNMICRC) માં સહાયક પ્રાધ્યાપક તરીકે સેવા આપતા, ડૉ. પરમાર અદ્યતન દર્દી સંભાળ અને ભાવિ કાર્ડિયોલોજિસ્ટના શિક્ષણમાં ઊંડાણથી સંકળાયેલા છે.",
      lead2: "તેમનો ક્લિનિકલ અભિગમ પુરાવા-આધારિત દવા પર આધારિત છે, જે દરેક દર્દીને અનુકૂળ અને અસરકારક સારવાર યોજના સુનિશ્ચિત કરે છે.",
      videoKicker: "કેથ લેબમાંથી",
      videoTitle: "મુશ્કેલ BMV માં ટિપ્સ અને ટ્રિક્સ",
      mediaLink: "મીડિયા લાઇબ્રેરી",
    },
    chapters: [
      { num: "1", title: "ચિકિત્સક", body: "ડૉ. દેવરતસિંહ પરમાર જટિલ અને દુર્લભ હૃદયરોગોના સંચાલનમાં ઊંડો રસ ધરાવતા સમર્પિત કાર્ડિયોલોજિસ્ટ છે. હાલમાં અમદાવાદની પ્રતિષ્ઠિત યુ.એન. મહેતા ઇન્સ્ટિટ્યૂટ ઑફ કાર્ડિયોલોજીમાં સહાયક પ્રાધ્યાપક તરીકે તેઓ અદ્યતન દર્દી સંભાળ અને શિક્ષણમાં સંકળાયેલા છે." },
      { num: "2", title: "પાયો", body: "MBBS, જનરલ મેડિસિનમાં MD અને કાર્ડિયોલોજીમાં DM — વ્યાપક તબીબી તાલીમ સાથે ડૉ. પરમારે ઉચ્ચ-જોખમી હૃદય સ્થિતિઓના નિદાન અને સારવારમાં મજબૂત પાયો બનાવ્યો છે." },
      { num: "3", title: "નિષ્ણાત", body: "નિયમિત કાર્ડિયોલોજી ઉપરાંત, ડૉ. પરમાર EVAR અને કેરોટિડ આર્ટરી સ્ટેન્ટિંગ જેવી અદ્યતન એન્ડોવાસ્ક્યુલર પ્રક્રિયાઓ તથા દુર્લભ કાર્ડિયોમાયોપથી અને એરિથમિયાના સંચાલનમાં વિશેષ નિપુણતા ધરાવે છે." },
    ],
    award: {
      kicker: "સન્માન",
      badge: "એવોર્ડ્સ અને સન્માન",
      heading: "એવોર્ડ્સ અને સન્માન",
      sub: "ઇન્ટરવેન્શનલ કાર્ડિયોલોજી, શૈક્ષણિક નેતૃત્વ અને દયાળુ દર્દી સંભાળમાં શ્રેષ્ઠતા માટે સન્માનિત.",
    },
    services: {
      kicker: "ક્લિનિકલ નિપુણતા",
      title1: "વિશિષ્ટ કાર્ડિયાક", title2: "સારવાર",
      viewAll: "બધું જુઓ",
      pageTitle1: "વિશિષ્ટ", pageTitle2: "કાર્ડિયાક કેર",
      pageSub: "જટિલ કેથેટર-આધારિત ઇન્ટરવેન્શનથી નિવારક જોખમ મૂલ્યાંકન સુધી — સંપૂર્ણ કાર્ડિયોવાસ્ક્યુલર સ્પેક્ટ્રમમાં પુરાવા-આધારિત સારવાર.",
      ctaTitle: "આજે જ તમારી હૃદય સંબંધિત ચિંતા વિશે વાત કરો.",
      viewResearch: "સંશોધન જુઓ",
      items: [
        { title: "ઇન્ટરવેન્શનલ કાર્ડિયોલોજી અને વાહિની પ્રક્રિયાઓ", body: "કેરોટિડ આર્ટરી સ્ટેન્ટિંગ અને મુશ્કેલ વાહિની પ્રવેશ કેસો માટે EVAR સહિત જટિલ કેથેટર-આધારિત સારવારમાં નિપુણતા." },
        { title: "હાર્ટ ફેલ્યોર અને કાર્ડિયોમાયોપથી સંચાલન", body: "રિસ્ટ્રિક્ટિવ કાર્ડિયોમાયોપથી, ગંભીર LV ડિસફંક્શન અને મોનોક્લોનલ ગેમોપથી જેવી પ્રણાલીગત બીમારીઓનું અદ્યતન નિદાન અને સારવાર." },
        { title: "એક્યુટ કોરોનરી સિન્ડ્રોમ અને ઇસ્કેમિક હૃદયરોગ", body: "હાર્ટ એટેક, એન્જાઇના અને કૌનિસ સિન્ડ્રોમ જેવા વિશિષ્ટ કાર્ડિયાક કેસો માટે વ્યાપક સંભાળ." },
        { title: "એરિથમિયા અને ડિવાઇસ ઇમ્પ્લાન્ટેશન", body: "જીવલેણ એરિથમિયાનું સંચાલન અને AICD જેવા જીવનરક્ષક ઉપકરણોનું ઇમ્પ્લાન્ટેશન." },
        { title: "નિવારક કાર્ડિયોલોજી અને જોખમ મૂલ્યાંકન", body: "કોરોનરી આર્ટરી રોગ ગંભીર બને તે પહેલાં નવીનતમ ક્લિનિકલ સ્કોરિંગ સિસ્ટમ દ્વારા જોખમ પરિબળોનું મૂલ્યાંકન." },
      ],
    },
    research: {
      kicker: "શૈક્ષણિક યોગદાન",
      title: "કાર્ડિયોવાસ્ક્યુલર વિજ્ઞાનને આગળ વધારવું",
      viewAll: "બધા પ્રકાશનો અને કેસ રિપોર્ટ્સ",
      pageTitle1: "સંશોધન અને", pageTitle2: "પ્રકાશનો",
      pageSub: "સતત કાર્ડિયાક શિક્ષણમાં અગ્રેસર — કાર્ડિયોલોજિકલ સોસાયટી ઑફ ઇન્ડિયા (CSI) માં પ્રસ્તુતિ અને આંતરરાષ્ટ્રીય જર્નલમાં યોગદાન.",
      pubHeading: "પસંદ કરેલા ઉચ્ચ-પ્રભાવ પ્રકાશનો",
      caseHeading: "નોંધપાત્ર કેસ રિપોર્ટ્સ અને પ્રસ્તુતિઓ",
      videoKicker: "શૈક્ષણિક સત્ર",
      videoTitle: "મુશ્કેલ બલૂન મિટ્રલ વાલ્વ્યુલોપ્લાસ્ટીમાં ટિપ્સ અને ટ્રિક્સ",
      videoSub: "કેથ લેબમાં પડકારજનક BMV કેસો માટે ટેકનિકો શેર કરતું વ્યવહારુ પ્રક્રિયા માર્ગદર્શન.",
      caseLabel: "કેસ",
    },
    media: {
      kicker: "વિડિયો અને મીડિયા",
      title: "મુશ્કેલ BMV માં ટિપ્સ અને ટ્રિક્સ",
      link: "મીડિયા લાઇબ્રેરી",
      pageTitle1: "મીડિયા", pageTitle2: "લાઇબ્રેરી",
      pageSub: "આગામી પેઢીના કાર્ડિયોલોજિસ્ટ માટે શેર કરેલી શૈક્ષણિક કાર્ડિયોલોજી સામગ્રી.",
      featured: "ફીચર્ડ",
      h: [
        { k: "વિષય", v: "બલૂન મિટ્રલ વાલ્વ્યુલોપ્લાસ્ટી" },
        { k: "ફોર્મેટ", v: "પ્રક્રિયા શિક્ષણ" },
        { k: "પ્રેક્ષક", v: "કાર્ડિયોલોજી ફેલો અને સાથીઓ" },
      ],
    },
    testimonials: {
      kicker: "દર્દીઓનો વિશ્વાસ",
      title: "જેમની સંભાળ લીધી તેમના શબ્દો",
      items: [
        { name: "રમેશ પટેલ", place: "અમદાવાદ", text: "ડૉ. પરમારે મારી એન્જિયોપ્લાસ્ટી શાંતિથી અને સ્પષ્ટ સમજાવી. બે વર્ષ પછી, મારું હૃદય પહેલા કરતાં મજબૂત લાગે છે. હંમેશા આભારી." },
        { name: "સુનિતા શાહ", place: "વડોદરા", text: "તેમણે એક દુર્લભ હૃદય સ્થિતિ શોધી કાઢી જે અન્ય ચૂકી ગયા હતા. તેમની ચોકસાઈ અને હૂંફે ખરેખર મારો જીવ બચાવ્યો." },
        { name: "ઇમરાન શેખ", place: "રાજકોટ", text: "મારા પિતાના AICD ઇમ્પ્લાન્ટ પછી, ડૉ. પરમાર દરેક કલાકે ઉપલબ્ધ હતા. આટલા સમર્પિત ડૉક્ટર મળવા દુર્લભ છે." },
        { name: "કિરણ દેસાઈ", place: "સુરત", text: "નિદાનથી સ્વસ્થ થવા સુધી, હું હંમેશા સુરક્ષિત, નિષ્ણાત હાથમાં અનુભવ્યું. તેઓ દર્દીઓ સાથે પરિવાર જેવો વ્યવહાર કરે છે." },
        { name: "મીના જોષી", place: "ગાંધીનગર", text: "દયાળુ, સંપૂર્ણ અને પ્રામાણિક. તેમણે ક્યારેય એક પણ પરામર્શમાં ઉતાવળ કરી નહીં. ખૂબ ભલામણ કરું છું." },
      ],
    },
    contact: {
      kicker: "મારો સંપર્ક કરો",
      title: "સંપર્ક કરો",
      pageSub: "કાર્ડિયાક પરામર્શ અને રેફરલ માટે ચોવીસે કલાક ઉપલબ્ધ.",
      call: "સીધો કૉલ કરો", email: "ઈમેલ મોકલો",
      hoursLabel: "કામકાજના કલાકો", hoursNote: "કાર્ડિયાક ઇમરજન્સી માટે હંમેશા ઉપલબ્ધ.",
      locLabel: "મુખ્ય સ્થાન",
      locName: "યુ. એન. મહેતા ઇન્સ્ટિટ્યૂટ ઑફ કાર્ડિયોલોજી અને રિસર્ચ સેન્ટર",
      locAddr: "સિવિલ હોસ્પિટલ કેમ્પસ, અસારવા, અમદાવાદ, ગુજરાત",
      dirLabel: "દિશા-નિર્દેશ", maps: "ગૂગલ મેપ્સમાં ખોલો",
      emergency: "ઇમરજન્સી સંપર્ક:",
      emergencyBody: "કાર્ડિયાક ઇમરજન્સીના કિસ્સામાં, કૃપા કરીને તરત જ નજીકની હોસ્પિટલના કેઝ્યુઅલ્ટી વિભાગમાં જાઓ.",
    },
    footer: { care: "કાર્ડિયોવાસ્ક્યુલર કેર", navigate: "નેવિગેટ", reach: "સંપર્ક કરો", tagline: "ચોકસાઈ · સંશોધન · સહાનુભૂતિ",
      bio: "યુ.એન. મહેતા ઇન્સ્ટિટ્યૂટ ઑફ કાર્ડિયોલોજીમાં સહાયક પ્રાધ્યાપક. ચોકસાઈ અને સહાનુભૂતિ સાથે અદ્યતન કાર્ડિયોવાસ્ક્યુલર સંભાળ." },
    whatsapp: "વોટ્સએપ પર ચેટ કરો",
    topbarRole: "ઇન્ટરવેન્શનલ કાર્ડિયોલોજિસ્ટ",
    hospital: {
      kicker: "નેતૃત્વ",
      role: "ડિરેક્ટર",
      title: "ગોધરા હાર્ટ એન્ડ સુપર સ્પેશિયાલિટી હોસ્પિટલ",
      tagline: "જ્યાં દરેક ધબકારા મહત્ત્વના છે.",
      lead: "ડૉ. દેવરતસિંહ પરમાર ગોધરા હાર્ટ એન્ડ સુપર સ્પેશિયાલિટી હોસ્પિટલ LLP ના ડિરેક્ટર છે — ગોધરા શહેરની એકમાત્ર કોર્પોરેટ મલ્ટિ-સુપરસ્પેશિયાલિટી 100 બેડની હોસ્પિટલ, જે એક જ છત નીચે વિશ્વસ્તરીય તબીબી સંભાળ પૂરી પાડે છે.",
      body: "સુલભ શ્રેષ્ઠતાની દ્રષ્ટિ પર આધારિત, આ હોસ્પિટલ ગોધરા અને સમગ્ર પંચમહાલ પ્રદેશના લોકો માટે અદ્યતન કાર્ડિયાક સંભાળ, સર્જિકલ નિપુણતા અને ક્રિટિકલ કેર માળખું લાવે છે — દરેક સારવારના કેન્દ્રમાં કરુણા સાથે.",
      beds: "બેડની હોસ્પિટલ",
      corporate: "કોર્પોરેટ મલ્ટિ-સુપરસ્પેશિયાલિટી",
      roof: "એક જ છત નીચે વિશ્વસ્તરીય સુવિધાઓ",
      scopeTitle: "અમારી સેવાઓની શ્રેણી",
      services: ["ઇન્ટરવેન્શનલ કાર્ડિયોલોજી", "નોન-ઇન્ટરવેન્શનલ કાર્ડિયોલોજી", "કાર્ડિયોથોરેસિક અને વેસ્ક્યુલર સર્જરી", "ઈએનટી (કાન-નાક-ગળા)", "હેડ એન્ડ નેક ઓન્કોલોજી", "ઓન્કોલોજી સર્જરી", "જનરલ મેડિસિન", "જનરલ અને લેપ્રોસ્કોપિક સર્જરી", "મેક્સિલોફેશિયલ સર્જરી", "ક્રિટિકલ કેર"],
      addressLabel: "સરનામું",
      callLabel: "હોસ્પિટલ પૂછપરછ",
    },
  },

  hi: {
    nav: { home: "होम", about: "परिचय", expertise: "विशेषज्ञता", research: "अनुसंधान", media: "मीडिया", contact: "संपर्क", consult: "परामर्श" },
    hero: {
      kicker: "इंटरवेंशनल कार्डियोलॉजिस्ट",
      l1: "एडवांस्ड", l2: "कार्डियक केयर", l3: "सटीकता के साथ.",
      desc: "डॉ. देवरतसिंह परमार जटिल हृदय एवं वाहिका रोगों के उपचार हेतु नवीनतम चिकित्सा अनुसंधान और उन्नत इंटरवेंशनल तकनीकों का उपयोग करते हैं — हर कदम पर सहानुभूति के साथ.",
      cta1: "विशेषज्ञता देखें", cta2: "संपर्क करें", scroll: "स्क्रॉल",
    },
    stats: [
      { value: "15+", label: "पीयर-रिव्यूड प्रकाशन" },
      { value: "24/7", label: "कार्डियक उपलब्धता" },
      { value: "DM", label: "कार्डियोलॉजी में डॉक्टरेट" },
      { value: "CSI", label: "राष्ट्रीय सम्मेलन प्रस्तुतकर्ता" },
    ],
    about: {
      kicker: "डॉ. परमार के बारे में",
      title: "जटिल हृदय के प्रति समर्पित चिकित्सक",
      role: "कार्डियोलॉजी में सहायक प्रोफेसर",
      city: "अहमदाबाद, गुजरात, भारत",
      body: "डॉ. देवरतसिंह परमार जटिल और दुर्लभ हृदय रोगों के प्रबंधन में गहरी रुचि रखने वाले समर्पित कार्डियोलॉजिस्ट हैं। वर्तमान में अहमदाबाद के प्रतिष्ठित यू.एन. मेहता इंस्टिट्यूट ऑफ कार्डियोलॉजी में सहायक प्रोफेसर के रूप में सेवारत, वे उन्नत रोगी देखभाल और भावी कार्डियोलॉजिस्ट के शिक्षण दोनों में गहराई से जुड़े हैं।",
      readMore: "पूरी कहानी पढ़ें",
    },
    aboutPage: {
      kicker: "डॉ. परमार के बारे में",
      t1: "मिलिए डॉ.", t2: "देवरतसिंह परमार",
      subtitle: "MBBS · MD · DM (कार्डियोलॉजी) — जटिल और दुर्लभ हृदय रोगों के प्रबंधन में गहरी रुचि रखने वाले समर्पित कार्डियोलॉजिस्ट।",
      lead: "वर्तमान में प्रतिष्ठित यू.एन. मेहता इंस्टिट्यूट ऑफ कार्डियोलॉजी (UNMICRC) में सहायक प्रोफेसर के रूप में सेवारत, डॉ. परमार उन्नत रोगी देखभाल और शिक्षण में गहराई से जुड़े हैं।",
      lead2: "उनका नैदानिक दृष्टिकोण साक्ष्य-आधारित चिकित्सा पर आधारित है, जो प्रत्येक रोगी को अनुकूलित और प्रभावी उपचार योजना सुनिश्चित करता है।",
      videoKicker: "कैथ लैब से",
      videoTitle: "कठिन BMV में टिप्स और ट्रिक्स",
      mediaLink: "मीडिया लाइब्रेरी",
    },
    chapters: [
      { num: "1", title: "चिकित्सक", body: "डॉ. देवरतसिंह परमार जटिल और दुर्लभ हृदय रोगों के प्रबंधन में गहरी रुचि रखने वाले समर्पित कार्डियोलॉजिस्ट हैं। अहमदाबाद के प्रतिष्ठित यू.एन. मेहता इंस्टिट्यूट में सहायक प्रोफेसर के रूप में वे उन्नत देखभाल और शिक्षण में जुड़े हैं।" },
      { num: "2", title: "आधार", body: "MBBS, जनरल मेडिसिन में MD और कार्डियोलॉजी में DM — व्यापक चिकित्सा प्रशिक्षण के साथ डॉ. परमार ने उच्च-जोखिम हृदय स्थितियों के निदान और उपचार में मजबूत आधार बनाया है।" },
      { num: "3", title: "विशेषज्ञ", body: "नियमित कार्डियोलॉजी के अलावा, डॉ. परमार EVAR और कैरोटिड आर्टरी स्टेंटिंग जैसी उन्नत एंडोवैस्कुलर प्रक्रियाओं तथा दुर्लभ कार्डियोमायोपैथी और अतालता के प्रबंधन में विशेष विशेषज्ञता रखते हैं।" },
    ],
    award: {
      kicker: "सम्मान",
      badge: "पुरस्कार एवं सम्मान",
      heading: "पुरस्कार एवं सम्मान",
      sub: "इंटरवेंशनल कार्डियोलॉजी, शैक्षणिक नेतृत्व और करुणामय रोगी देखभाल में उत्कृष्टता हेतु सम्मानित।",
    },
    services: {
      kicker: "नैदानिक विशेषज्ञता",
      title1: "विशिष्ट कार्डियक", title2: "उपचार",
      viewAll: "सभी देखें",
      pageTitle1: "विशिष्ट", pageTitle2: "कार्डियक केयर",
      pageSub: "जटिल कैथेटर-आधारित इंटरवेंशन से लेकर निवारक जोखिम मूल्यांकन तक — संपूर्ण कार्डियोवैस्कुलर क्षेत्र में साक्ष्य-आधारित उपचार।",
      ctaTitle: "आज ही अपनी हृदय संबंधी चिंता पर बात करें।",
      viewResearch: "अनुसंधान देखें",
      items: [
        { title: "इंटरवेंशनल कार्डियोलॉजी और वाहिका प्रक्रियाएं", body: "कैरोटिड आर्टरी स्टेंटिंग और कठिन वाहिका पहुँच मामलों के लिए EVAR सहित जटिल कैथेटर-आधारित उपचार में विशेषज्ञता।" },
        { title: "हार्ट फेलियर और कार्डियोमायोपैथी प्रबंधन", body: "रिस्ट्रिक्टिव कार्डियोमायोपैथी, गंभीर LV डिसफंक्शन और मोनोक्लोनल गैमोपैथी जैसी प्रणालीगत बीमारियों का उन्नत निदान और उपचार।" },
        { title: "एक्यूट कोरोनरी सिंड्रोम और इस्केमिक हृदय रोग", body: "हार्ट अटैक, एनजाइना और कौनिस सिंड्रोम जैसे विशिष्ट कार्डियक मामलों के लिए व्यापक देखभाल।" },
        { title: "अतालता और डिवाइस इम्प्लांटेशन", body: "जानलेवा अतालता का प्रबंधन और AICD जैसे जीवनरक्षक उपकरणों का इम्प्लांटेशन।" },
        { title: "निवारक कार्डियोलॉजी और जोखिम मूल्यांकन", body: "कोरोनरी आर्टरी रोग के गंभीर होने से पहले नवीनतम क्लिनिकल स्कोरिंग प्रणाली द्वारा जोखिम कारकों का मूल्यांकन।" },
      ],
    },
    research: {
      kicker: "शैक्षणिक योगदान",
      title: "कार्डियोवैस्कुलर विज्ञान को आगे बढ़ाना",
      viewAll: "सभी प्रकाशन और केस रिपोर्ट",
      pageTitle1: "अनुसंधान और", pageTitle2: "प्रकाशन",
      pageSub: "निरंतर कार्डियक शिक्षा में अग्रणी — कार्डियोलॉजिकल सोसाइटी ऑफ इंडिया (CSI) में प्रस्तुति और अंतरराष्ट्रीय जर्नल में योगदान।",
      pubHeading: "चयनित उच्च-प्रभाव प्रकाशन",
      caseHeading: "उल्लेखनीय केस रिपोर्ट और प्रस्तुतियाँ",
      videoKicker: "शैक्षणिक सत्र",
      videoTitle: "कठिन बैलून माइट्रल वाल्वुलोप्लास्टी में टिप्स और ट्रिक्स",
      videoSub: "कैथ लैब में चुनौतीपूर्ण BMV मामलों के लिए तकनीकें साझा करने वाला व्यावहारिक प्रक्रिया मार्गदर्शन।",
      caseLabel: "केस",
    },
    media: {
      kicker: "वीडियो और मीडिया",
      title: "कठिन BMV में टिप्स और ट्रिक्स",
      link: "मीडिया लाइब्रेरी",
      pageTitle1: "मीडिया", pageTitle2: "लाइब्रेरी",
      pageSub: "अगली पीढ़ी के कार्डियोलॉजिस्ट के लिए साझा की गई शैक्षणिक कार्डियोलॉजी सामग्री।",
      featured: "फीचर्ड",
      h: [
        { k: "विषय", v: "बैलून माइट्रल वाल्वुलोप्लास्टी" },
        { k: "प्रारूप", v: "प्रक्रिया शिक्षण" },
        { k: "दर्शक", v: "कार्डियोलॉजी फेलो और सहयोगी" },
      ],
    },
    testimonials: {
      kicker: "रोगियों का विश्वास",
      title: "जिनकी देखभाल की, उनके शब्द",
      items: [
        { name: "रमेश पटेल", place: "अहमदाबाद", text: "डॉ. परमार ने मेरी एंजियोप्लास्टी शांति और स्पष्टता से समझाई। दो साल बाद, मेरा दिल पहले से कहीं मजबूत महसूस होता है। सदा आभारी।" },
        { name: "सुनीता शाह", place: "वडोदरा", text: "उन्होंने एक दुर्लभ हृदय स्थिति का पता लगाया जिसे अन्य चूक गए थे। उनकी सटीकता और गर्मजोशी ने सचमुच मेरी जान बचाई।" },
        { name: "इमरान शेख", place: "राजकोट", text: "मेरे पिता के AICD इम्प्लांट के बाद, डॉ. परमार हर समय उपलब्ध रहे। इतना समर्पित डॉक्टर मिलना दुर्लभ है।" },
        { name: "किरण देसाई", place: "सूरत", text: "निदान से लेकर स्वस्थ होने तक, मैं हमेशा सुरक्षित, विशेषज्ञ हाथों में महसूस करता रहा। वे रोगियों के साथ परिवार जैसा व्यवहार करते हैं।" },
        { name: "मीना जोशी", place: "गांधीनगर", text: "करुणामय, संपूर्ण और ईमानदार। उन्होंने कभी किसी परामर्श में जल्दबाज़ी नहीं की। अत्यधिक अनुशंसित।" },
      ],
    },
    contact: {
      kicker: "मुझसे संपर्क करें",
      title: "संपर्क करें",
      pageSub: "कार्डियक परामर्श और रेफरल हेतु चौबीसों घंटे उपलब्ध।",
      call: "सीधे कॉल करें", email: "ईमेल भेजें",
      hoursLabel: "कार्य समय", hoursNote: "कार्डियक आपातकाल हेतु सदैव उपलब्ध।",
      locLabel: "मुख्य स्थान",
      locName: "यू. एन. मेहता इंस्टिट्यूट ऑफ कार्डियोलॉजी एवं रिसर्च सेंटर",
      locAddr: "सिविल हॉस्पिटल कैम्पस, असारवा, अहमदाबाद, गुजरात",
      dirLabel: "दिशा-निर्देश", maps: "गूगल मैप्स में खोलें",
      emergency: "आपातकालीन संपर्क:",
      emergencyBody: "कार्डियक आपातकाल की स्थिति में, कृपया तुरंत निकटतम अस्पताल के कैज़ुअल्टी विभाग में जाएँ।",
    },
    footer: { care: "कार्डियोवैस्कुलर केयर", navigate: "नेविगेट", reach: "संपर्क करें", tagline: "सटीकता · अनुसंधान · सहानुभूति",
      bio: "यू.एन. मेहता इंस्टिट्यूट ऑफ कार्डियोलॉजी में सहायक प्रोफेसर। सटीकता और सहानुभूति के साथ उन्नत कार्डियोवैस्कुलर देखभाल।" },
    whatsapp: "व्हाट्सएप पर चैट करें",
    topbarRole: "इंटरवेंशनल कार्डियोलॉजिस्ट",
    hospital: {
      kicker: "नेतृत्व",
      role: "निदेशक",
      title: "गोधरा हार्ट एंड सुपर स्पेशियलिटी हॉस्पिटल",
      tagline: "जहाँ हर धड़कन मायने रखती है.",
      lead: "डॉ. देवरतसिंह परमार गोधरा हार्ट एंड सुपर स्पेशियलिटी हॉस्पिटल LLP के निदेशक हैं — गोधरा शहर का एकमात्र कॉर्पोरेट मल्टी-सुपरस्पेशियलिटी 100 बेड वाला अस्पताल, जो एक ही छत के नीचे विश्वस्तरीय चिकित्सा सेवा प्रदान करता है।",
      body: "सुलभ उत्कृष्टता की दृष्टि पर आधारित, यह अस्पताल गोधरा और समूचे पंचमहाल क्षेत्र के लोगों के लिए उन्नत कार्डियक देखभाल, सर्जिकल विशेषज्ञता और क्रिटिकल केयर अवसंरचना लाता है — हर उपचार के केंद्र में करुणा के साथ।",
      beds: "बेड वाला अस्पताल",
      corporate: "कॉर्पोरेट मल्टी-सुपरस्पेशियलिटी",
      roof: "एक ही छत के नीचे विश्वस्तरीय सुविधाएँ",
      scopeTitle: "हमारी सेवाओं का दायरा",
      services: ["इंटरवेंशनल कार्डियोलॉजी", "नॉन-इंटरवेंशनल कार्डियोलॉजी", "कार्डियोथोरेसिक और वैस्कुलर सर्जरी", "ईएनटी (कान-नाक-गला)", "हेड एंड नेक ऑन्कोलॉजी", "ऑन्कोलॉजी सर्जरी", "जनरल मेडिसिन", "जनरल और लेप्रोस्कोपिक सर्जरी", "मैक्सिलोफेशियल सर्जरी", "क्रिटिकल केयर"],
      addressLabel: "पता",
      callLabel: "अस्पताल पूछताछ",
    },
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("drdp_lang") || "en");
  useEffect(() => {
    localStorage.setItem("drdp_lang", lang);
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);
  const t = TR[lang] || TR.en;
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
